import PropTypes from 'prop-types';
import React from 'react';
import { Client, HydrationProvider } from 'react-hydration-provider';
import ReactPlayer from 'react-player';

import { useWidowKiller } from '../../tools/hooks/useWidowKiller';
import { getThemeClass } from '../../tools/textTools';
import ContentfulImage from '../Common/ContentfulImage';
import RichTextConverter from '../Common/RichText/RichTextConverter';
import HeadingCtas from '../Generic/HeadingCtas';
import PageSection from '../PageSections/PageSection';
import * as styles from './HalfAndHalfHeader.module.scss';

const HalfAndHalfHeader = ({ fields, featured, slim = false, verticalBleed = false, wrapClassName = '' }) => {
  const themeClass = getThemeClass(fields.headingTheme?.themeName);
  let bg = {};
  if (fields.backgroundImage) {
    bg = { '--backgroundImage': `url('${fields.backgroundImage.file?.url}')` };
  }
  const wrappedTitle = useWidowKiller(fields.heading);

  return (
    <PageSection
      isTopSection={true}
      data-analytics-section={'half-and-half-header'}
      className={[
        styles.header,
        verticalBleed ? styles.verticalBleed : '',
        !!fields.backgroundImage ? styles.section : '',
        slim ? styles.slim : '',
        wrapClassName,
        themeClass,
      ].join(' ')}
      style={bg}
    >
      <div className={styles.grid}>
        <div
          data-analytics-component={'header-copy-area'}
          data-analytics-id={fields.headingSuper}
          className={[styles.headingContent, fields.headingFontColor === 'Dark' ? styles.dark : ''].join(' ')}
        >
          {fields.headingSuper ? (
            <>
              <h1 className={[styles.headingSuper, featured ? styles.featuredSuper : ''].join(' ')}>
                {featured && (
                  <>
                    <span className={styles.featuredSuperGold}>✦ FEATURED</span>
                    <span> • </span>
                  </>
                )}
                {fields.headingSuper}
              </h1>
              <h2 className={['homepage-h1', styles.useH1].join(' ')}>{wrappedTitle}</h2>
            </>
          ) : (
            <h1 className={'homepage-h1'}>{wrappedTitle}</h1>
          )}
          {fields.lede?.raw && <RichTextConverter className={'homepage-lede'} copy={fields.lede} />}
          {fields.lede?.childMarkdownRemark && (
            <div
              className={'homepage-lede'}
              dangerouslySetInnerHTML={{ __html: fields.lede?.childMarkdownRemark.html }}
            />
          )}
          {fields.ctas?.length > 0 && <HeadingCtas ctas={fields.ctas} />}
        </div>
        {!fields.videoUrl && fields.heroImage && (
          <ContentfulImage
            lazy={false}
            fetchpriority={'high'}
            className={['homepage-hero', styles.heroImage].join(' ')}
            imageFragment={fields.heroImage}
            alt={'hero'}
          />
        )}
        {fields.videoUrl && (
          <div className={styles.wistiaWrap}>
            <div className={`player-wrapper-${fields.videoUrl}`}>
              <HydrationProvider>
                <Client>
                  <ReactPlayer
                    className={`react-player-${fields.videoUrl}`}
                    width={'100%'}
                    height="100%"
                    controls={true}
                    url={fields.videoUrl}
                  />
                </Client>
              </HydrationProvider>
            </div>
          </div>
        )}
      </div>
    </PageSection>
  );
};

HalfAndHalfHeader.propTypes = {
  fields: PropTypes.object.isRequired,
  verticalBleed: PropTypes.bool.isRequired,
};

export default HalfAndHalfHeader;
